// @flow
import type { Action, ContextState, Dispatch, PlanType, WebBillingDestination } from '@types';
import { reportError, isCordovaApp, openBillingPage, globalT } from '@helpers/helpers';
import { get } from 'lodash';
import { Api } from '../../../helpers';
import { router } from '../../../routes/router';
import { getUser } from './user';


// ------------------------------------
// Constants
// ------------------------------------
export const SUBSCRIPTION_WEB_LINK_LOADING = 'subscription.SUBSCRIPTION_WEB_LINK_LOADING';
export const SUBSCRIPTION_WEB_LINK_LOADED = 'subscription.SUBSCRIPTION_WEB_LINK_LOADED';
export const SUBSCRIPTION_WEB_LINK_ERROR = 'subscription.SUBSCRIPTION_WEB_LINK_ERROR';
export const SUBSCRIPTION_WEB_CHECKOUT_CHECKING = 'subscription.SUBSCRIPTION_WEB_CHECKOUT_CHECKING';
export const SUBSCRIPTION_WEB_CHECKOUT_CHECKED = 'subscription.SUBSCRIPTION_WEB_CHECKOUT_CHECKED';
export const SUBSCRIPTION_WEB_CHECKOUT_DONE = 'subscription.SUBSCRIPTION_WEB_CHECKOUT_DONE';

// Coming back to the app within this time of opening a billing page counts as coming back from it
export const WEB_BILLING_RETURN_WINDOW = 60 * 60 * 1000;
// axios has no timeout: a request that never answers must not hold the check (and its spinner)
export const WEB_CHECKOUT_CHECK_TIMEOUT = 20 * 1000;

export const SUBSCRIPTION_GET_TRACK_INFO_LOADING = 'subscription.SUBSCRIPTION_GET_TRACK_INFO_LOADING';
export const SUBSCRIPTION_GET_TRACK_INFO_LOADED = 'subscription.SUBSCRIPTION_GET_TRACK_INFO_LOADED';
export const SUBSCRIPTION_GET_TRACK_INFO_ERROR = 'subscription.SUBSCRIPTION_GET_TRACK_INFO_ERROR';

export const SUBSCRIPTION_GET_USER_INFO_INFO_LOADING = 'subscription.SUBSCRIPTION_GET_USER_INFO_INFO_LOADING';
export const SUBSCRIPTION_GET_USER_INFO_INFO_LOADED = 'subscription.SUBSCRIPTION_GET_USER_INFO_INFO_LOADED';
export const SUBSCRIPTION_GET_USER_INFO_INFO_ERROR = 'subscription.SUBSCRIPTION_GET_TRACK_INFO_ERROR';

const SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADING = 'subscription.SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADING';
const SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADED = 'subscription.SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADED';
const SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_FAILED = 'subscription.SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_FAILED';

const SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADING = 'subscription.SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADING';
const SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADED = 'subscription.SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADED';
const SUBSCRIPTION_REQUEST_NEW_BOLETO_FAILED = 'subscription.SUBSCRIPTION_REQUEST_NEW_BOLETO_FAILED';

// ------------------------------------
// Actions
// ------------------------------------


export const getSubscriptionTrack = (trackName: string) =>
  (dispatch: Dispatch) => {
    dispatch({ type: SUBSCRIPTION_GET_TRACK_INFO_LOADING });

    Api.get(router.getApiRoute('getSubscriptionTrack', { trackName }), {
      success: (response) => {
        dispatch({
          type: SUBSCRIPTION_GET_TRACK_INFO_LOADED,
          data: get(response, 'subscription'),
          trackName,
        });
      },
      error: (error) => {
        dispatch({ type: SUBSCRIPTION_GET_TRACK_INFO_ERROR, error: { message: error.message } });
      },
    });
  };


export const getActiveSubscriptionUserInfo = () =>
  (dispatch: Dispatch) => {
    dispatch({ type: SUBSCRIPTION_GET_USER_INFO_INFO_LOADING });

    Api.get(router.getApiRoute('getActiveSubscriptionUserInfo', { }), {
      success: (response) => {
        dispatch({
          type: SUBSCRIPTION_GET_USER_INFO_INFO_LOADED,
          data: get(response, 'subscription', {}),
        });
      },
      error: (error) => {
        dispatch({ type: SUBSCRIPTION_GET_USER_INFO_INFO_ERROR, error: { message: error.message } });
      },
    });
  };

export const getSubscriptionUserInfo = () =>
  (dispatch: Dispatch) => {
    dispatch({ type: SUBSCRIPTION_GET_USER_INFO_INFO_LOADING });

    Api.get(router.getApiRoute('getSubscriptionUserInfo', { }), {
      success: (response) => {
        dispatch({
          type: SUBSCRIPTION_GET_USER_INFO_INFO_LOADED,
          data: get(response, 'subscription', {}),
        });
      },
      error: (error) => {
        dispatch({ type: SUBSCRIPTION_GET_USER_INFO_INFO_ERROR, error: { message: error.message } });
      },
    });
  };

// Plans and prices live in the app, but paying (checkout), changing the card and managing
// the plan happen on the API's billing pages, which it signs in through a short-lived,
// single-use link. Nothing is purchased inside the app.
export const openWebBilling = (destination: WebBillingDestination, plan: ?PlanType = null) =>
  (dispatch: Dispatch, getState: Function) => {
    // A second tap while the link is being created would open the browser twice
    if (get(getState(), ['context', 'subscription', 'openingWebBilling'])) return;

    const failed = (error) => {
      dispatch({ type: SUBSCRIPTION_WEB_LINK_ERROR, error: { message: error.message } });
      dispatch(reportError(error));
    };

    dispatch({ type: SUBSCRIPTION_WEB_LINK_LOADING, destination, plan });

    Api.post(router.getApiRoute('subscriptionWebLink', null), {
      data: { destination, plan, from: isCordovaApp() ? 'app' : 'web' },
      success: (response) => {
        if (!openBillingPage(get(response, ['data', 'url']))) {
          failed({ message: globalT('error/request', 'Ocorreu um erro ao executar esta requisição') });
          return;
        }
        // Only the native apps wait for the user to come back from Safari (listenForWebCheckoutReturn).
        // The web app leaves this page for the checkout, whose success page links back here.
        dispatch({ type: SUBSCRIPTION_WEB_LINK_LOADED, destination, plan, waitsForReturn: !!isCordovaApp() });
      },
      error: failed,
    });
  };

const subscriptionStatus = (state) => get(state, ['context', 'user', 'advancedData', 'subscriptionStatus']);
const openedWebBilling = (state) => get(state, ['context', 'subscription', 'webCheckout']);
const isRecent = ({ openedAt }) => Date.now() - Date.parse(openedAt) < WEB_BILLING_RETURN_WINDOW;

// The success screen replaces the plans page, never a page the user moved on to.
const isOnPlansPage = () => {
  const routes = router.getNamedRoutes();
  const path = window.location.hash.replace(/^#/, '').split('?')[0];
  return path === routes.pricing;
};

const withTimeout = (promise, ms) => new Promise((resolve) => {
  const timer = setTimeout(resolve, ms);
  Promise.resolve(promise).catch(() => {}).then(() => {
    clearTimeout(timer);
    resolve();
  });
});

let webCheckoutCheck = null;

// Refreshes the account when the user is back from a billing page in the browser and, once a
// checkout opened from the plans page is paid, shows the success screen. Blocked accounts are
// always refreshed: they may have paid some other way (the web app, a link from support).
// Coming back more than an hour after opening a page no longer counts as coming back from it,
// unless the user says so (`userAction`: the "Já assinei" button).
export const checkWebCheckout = ({ userAction = false }: { userAction?: boolean } = {}) =>
  (dispatch: Dispatch, getState: Function) => {
    const opened = openedWebBilling(getState());
    if (opened && !userAction && !isRecent(opened)) dispatch({ type: SUBSCRIPTION_WEB_CHECKOUT_DONE });

    const blocked = get(getState(), ['context', 'user', 'advancedData', 'blocked'], false);
    if (!openedWebBilling(getState()) && !blocked) return Promise.resolve(false);

    // Coming back can fire both events at once (resume, handleOpenURL)
    if (!webCheckoutCheck) {
      const statusBefore = subscriptionStatus(getState());
      dispatch({ type: SUBSCRIPTION_WEB_CHECKOUT_CHECKING });

      webCheckoutCheck = withTimeout(dispatch(getUser()), WEB_CHECKOUT_CHECK_TIMEOUT)
        .then(() => {
          const page = openedWebBilling(getState());
          const checkout = get(page, 'destination') === 'subscribe';
          const status = subscriptionStatus(getState());
          const subscribed = status === 'active';

          // The card and invoice pages only need this one refresh; a checkout waits until it is paid.
          if (page && (!checkout || subscribed)) dispatch({ type: SUBSCRIPTION_WEB_CHECKOUT_DONE });
          dispatch({ type: SUBSCRIPTION_WEB_CHECKOUT_CHECKED, unconfirmed: checkout && !subscribed });

          if (checkout && subscribed && isOnPlansPage()) {
            dispatch(router.pushPath('subscriptionComplete'));
          } else {
            // Paid some other way (the web app, a link from support): "Meu plano", not "Assinar" again
            if (subscribed && isOnPlansPage()) dispatch(router.pushPath('subscriptionInfo'));
            // The plan, card and warnings of "Meu plano"
            if (page || status !== statusBefore) dispatch(getActiveSubscriptionUserInfo());
          }
          return subscribed;
        })
        .finally(() => { webCheckoutCheck = null; });
    }
    return webCheckoutCheck;
  };

// Ways back to the native app from the browser: the app returns to the foreground ("resume")
// or is opened by the doutore:// link on the checkout's success page (handleOpenURL, see
// config.xml). A cold start needs neither: AppLayout loads the user again. The web app has no
// return either: the checkout's success page links to subscriptionComplete.
export const listenForWebCheckoutReturn = (dispatch: Dispatch) => {
  const check = () => dispatch(checkWebCheckout());

  document.addEventListener('resume', check);
  window.handleOpenURL = () => setTimeout(check, 0);
};


export const newBoleto = () => (
  (dispatch: Dispatch) => {
    dispatch({ type: SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADING });
    Api.post(router.getApiRoute('newBoleto', { }), {
      data: { },
      success: () => {
        dispatch({ type: SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADED });
        dispatch(getActiveSubscriptionUserInfo());
      },
      error: (error) => {
        dispatch({ type: SUBSCRIPTION_REQUEST_NEW_BOLETO_FAILED, error: { message: error.message } });
        dispatch(reportError(error));
      },
    });
  });

export const getAvailablePlans = () => (
  (dispatch: Dispatch) => {
    dispatch({ type: SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADING });
    Api.get(router.getApiRoute('getAvailablePlans', { }), {
      success: (response) => {
        dispatch({
          type: SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADED,
          plans: get(response, 'availablePlans', {}),
        });
      },
      error: (error) => {
        dispatch({
          type: SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_FAILED, error: { message: error.message },
        });
      },
    });
  }
);

export const subscriptionActions = {
  openWebBilling,
  checkWebCheckout,
  getSubscriptionTrack,
  getAvailablePlans,
};

// ------------------------------------
// Reducer
// ------------------------------------
export default function subscruptionReducer (state: ContextState, action: Action): ContextState {
  switch (action.type) {
    case SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADING:
      return state
        .setIn(['subscription', 'availablePlansLoading'], true)
        .setIn(['subscription', 'availablePlansError'], false);

    case SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_LOADED:
      return state
        .setIn(['subscription', 'availablePlansLoading'], false)
        .setIn(['subscription', 'availablePlansError'], false)
        .setIn(['subscription', 'availablePlans'], action.plans);

    case SUBSCRIPTION_USER_GET_AVAILABLE_PLANS_FAILED:
      return state
        .setIn(['subscription', 'availablePlansLoading'], false)
        .setIn(['subscription', 'availablePlansError'], true);


    case SUBSCRIPTION_WEB_LINK_LOADING:
      return state
        .setIn(['subscription', 'openingWebBilling'], { destination: action.destination, plan: action.plan });

    case SUBSCRIPTION_WEB_LINK_LOADED:
      return (action.waitsForReturn
        ? state
          .setIn(['subscription', 'webCheckout'], { destination: action.destination, plan: action.plan, openedAt: new Date().toISOString() })
          .setIn(['subscription', 'webCheckoutUnconfirmed'], false)
        : state)
        .setIn(['subscription', 'openingWebBilling'], null);

    case SUBSCRIPTION_WEB_LINK_ERROR:
      return state
        .setIn(['subscription', 'openingWebBilling'], null)
        .setIn(['subscription', 'errors'], [action.error.message]);

    case SUBSCRIPTION_WEB_CHECKOUT_CHECKING:
      return state
        .setIn(['subscription', 'isCheckingWebCheckout'], true);

    case SUBSCRIPTION_WEB_CHECKOUT_CHECKED:
      return state
        .setIn(['subscription', 'isCheckingWebCheckout'], false)
        .setIn(['subscription', 'webCheckoutUnconfirmed'], action.unconfirmed);

    case SUBSCRIPTION_WEB_CHECKOUT_DONE:
      return state
        .setIn(['subscription', 'webCheckout'], null)
        .setIn(['subscription', 'webCheckoutUnconfirmed'], false);


    case SUBSCRIPTION_GET_TRACK_INFO_LOADING:
      return state
        .setIn(['subscription', 'isLoading'], true);

    case SUBSCRIPTION_GET_TRACK_INFO_LOADED:
      return state
        .setIn(['subscription', 'trackInfo', action.trackName], action.data)
        .setIn(['subscription', 'isLoading'], false);

    case SUBSCRIPTION_GET_TRACK_INFO_ERROR:
      return state
        .setIn(['subscription', 'isLoading'], false)
        .setIn(['subscription', 'errors'], [action.error.message]);


    case SUBSCRIPTION_GET_USER_INFO_INFO_LOADING:
      return state
        .setIn(['subscription', 'isLoading'], true);

    case SUBSCRIPTION_GET_USER_INFO_INFO_LOADED:
      return state
        .setIn(['subscription', 'subscriptionData'], action.data)
        .setIn(['subscription', 'selectedPlan'], action.data.plan)
        .setIn(['subscription', 'planValue'], action.data.subscriptionPrice)
        .setIn(['subscription', 'userInfo'], get(action, ['data', 'userInfo'], {}))
        .setIn(['subscription', 'isLoading'], false);

    case SUBSCRIPTION_GET_USER_INFO_INFO_ERROR:
      return state
        .setIn(['subscription', 'isLoading'], false)
        .setIn(['subscription', 'errors'], [action.error.message]);

    case SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADING:
      return state
        .setIn(['subscription', 'isRequestingNewBoleto'], true);

    case SUBSCRIPTION_REQUEST_NEW_BOLETO_LOADED:
      return state
        .setIn(['subscription', 'isRequestingNewBoleto'], false);

    case SUBSCRIPTION_REQUEST_NEW_BOLETO_FAILED:
      return state
        .setIn(['subscription', 'isRequestingNewBoleto'], false)
        .setIn(['subscription', 'errors'], [action.error.message]);

    default:
      return state;
  }
}
